// Navigation structure for the sidebar.
// Maps top-level sections and their sub-items to routes.
// Labels use i18n translation keys (layout namespace).

import {
  ShieldAlert,
  Shield,
  ClipboardList,
  CircleCheck,
  Zap,
  BarChart3,
  UsersRound,
  Settings,
  Layers,
  Import,
  Building2,
  type LucideIcon,
} from 'lucide-react'

export interface NavItem {
  /** i18n key in the layout:sidebar namespace */
  labelKey: string
  path: string
  icon: LucideIcon
  children?: NavChild[]
  /** If true, only shown to tenant admins. */
  adminOnly?: boolean
}

export interface NavChild {
  /** i18n key in the layout:nav namespace */
  labelKey: string
  /** Route path (for leaf items) or prefix path (for sub-groups, used for active detection). */
  path: string
  /** If present, this child is a collapsible sub-group containing these items. */
  children?: NavChild[]
  /** If true, only shown to tenant admins. */
  adminOnly?: boolean
}

/**
 * Build the navigation items for a tenant.
 * Paths are relative to /t/{slug}/.
 * Labels are i18n keys — resolved in the Sidebar component via t().
 */
export function getNavItems(): NavItem[] {
  return [
    {
      labelKey: 'sidebar.risks',
      path: '/risks',
      icon: ShieldAlert,
    },
    {
      labelKey: 'sidebar.controls',
      path: '/controls',
      icon: Shield,
    },
    {
      labelKey: 'sidebar.obligations',
      path: '/obligations',
      icon: ClipboardList,
    },
    {
      labelKey: 'sidebar.actions',
      path: '/actions',
      icon: CircleCheck,
    },
    {
      labelKey: 'sidebar.events',
      path: '/events',
      icon: Zap,
    },
    {
      labelKey: 'sidebar.import',
      path: '/import',
      icon: Import,
    },
    {
      labelKey: 'sidebar.reporting',
      path: '/reporting',
      icon: BarChart3,
      children: [
        { labelKey: 'nav.riskHeatmap', path: '/reporting/risk-heatmap' },
        { labelKey: 'nav.controlAssessments', path: '/reporting/control-assessments' },
        { labelKey: 'nav.controlsSummary', path: '/reporting/controls-summary' },
        { labelKey: 'nav.recentEvents', path: '/reporting/events-last-period' },
      ],
    },
    {
      labelKey: 'sidebar.peopleAccess',
      path: '/admin',
      icon: UsersRound,
      adminOnly: true,
      children: [
        { labelKey: 'nav.users', path: '/admin/users' },
        { labelKey: 'nav.apiUsers', path: '/admin/api-users' },
        { labelKey: 'nav.ownershipRoles', path: '/admin/ownership-roles' },
        { labelKey: 'nav.roles', path: '/admin/roles' },
      ],
    },
    {
      labelKey: 'sidebar.framework',
      path: '/framework',
      icon: Layers,
      adminOnly: true,
      children: [
        { labelKey: 'nav.taxonomy', path: '/framework/taxonomy' },
        {
          labelKey: 'nav.riskScoring',
          path: '/framework/scoring',
          children: [
            { labelKey: 'nav.gridSize', path: '/framework/scoring-grid' },
            { labelKey: 'nav.likelihood', path: '/framework/likelihood' },
            { labelKey: 'nav.impactTables', path: '/framework/impact-tables' },
          ],
        },
        { labelKey: 'nav.appetite', path: '/framework/appetite' },
        { labelKey: 'nav.assessmentThresholds', path: '/framework/assessment-thresholds' },
        { labelKey: 'nav.evidenceTags', path: '/framework/evidence-tags' },
        {
          labelKey: 'nav.approvals',
          path: '/framework/approvals',
          children: [
            { labelKey: 'nav.controlApprovals', path: '/framework/approvals/controls' },
            { labelKey: 'nav.actionApprovals', path: '/framework/approvals/actions' },
          ],
        },
        {
          labelKey: 'nav.categoryStructures',
          path: '/framework/categories',
          children: [
            { labelKey: 'nav.registerStructure', path: '/framework/registers' },
            { labelKey: 'nav.controlCategories', path: '/framework/categories/control' },
            { labelKey: 'nav.eventCategories', path: '/framework/categories/event' },
            { labelKey: 'nav.actionCategories', path: '/framework/categories/action' },
            { labelKey: 'nav.frameworkCategories', path: '/framework/categories/framework' },
          ],
        },
      ],
    },
    {
      labelKey: 'sidebar.platform',
      path: '/platform',
      icon: Building2,
      adminOnly: true,
      children: [
        { labelKey: 'nav.subscription', path: '/platform/subscription' },
        { labelKey: 'nav.features', path: '/platform/features' },
        { labelKey: 'nav.authentication', path: '/platform/authentication', adminOnly: true },
      ],
    },
    {
      labelKey: 'sidebar.settings',
      path: '/settings',
      icon: Settings,
      children: [
        { labelKey: 'nav.apiKey', path: '/settings/api-key' },
        { labelKey: 'nav.branding', path: '/settings/branding', adminOnly: true },
      ],
    },
  ]
}
